import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { setMadadContactModalState } from 'store/state/app/actions';
import { SetMadadContactModalStatePayload } from 'store/state/app/payloads';
import { getOfficeLink } from 'helpers/contact';
import { AgencyCard } from './AgencyCard';
import { MadadAgencyCard } from '../entities';
import withEvents from 'analytics/withEvents';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import { Route } from 'config/routes';
import { Link } from 'components/link';
import { CardLink } from '../styled';
import { TopTenBadgeVariant } from 'components/madad/top-ten-badge';


interface WithDispatchProps {
  openContactModal: (payload: SetMadadContactModalStatePayload) => void;
  onCardClickEvent?: () => void;
  sectionInView?: boolean;
  rootNode?: React.MutableRefObject<HTMLDivElement>;
  onCardExposeEvent?: (id: string) => void;
  withPropertyInfo?: boolean;
  variantToImagePath?: Record<TopTenBadgeVariant, string>;
}

export const AgencySmartCard: React.FC<MadadAgencyCard & WithDispatchProps> = props => {
  const {
    id,
    phone,
    agencyName,
    avatarImage,
    openContactModal,
    isRegistered,
    onCardClickEvent,
    verifiedIconColor,
    sectionInView,
    rootNode,
    onCardExposeEvent,
    withPropertyInfo = false,
    variantToImagePath,
  } = props;

  const [ isVisible, setIsVisible ] = useState(false);
  const cardItem = useRef<HTMLDivElement>(null);

  useIntersectionObserver(([ entry ]) => {
    setIsVisible(entry.isIntersecting);
  }, [ cardItem ], { threshold: 0, root: rootNode });

  useEffect(() => {
    if (isVisible && sectionInView) {
      onCardExposeEvent(id);
    }
  }, [ onCardExposeEvent, isVisible, sectionInView, id ]);

  const href = getOfficeLink(id);

  const handleContactClick = () => {
    openContactModal({
      id,
      phone,
      isAgent: false,
      officeId: id,
      officeName: agencyName,
      officeLogoUrl: avatarImage,
      officeHref: href,
      eventSource: 'madad_card',
    });
  };

  const card = (
    <AgencyCard
      {...props}
      withPropertyInfo={withPropertyInfo}
      onContactClick={handleContactClick}
      verifiedIconColor={verifiedIconColor}
      variantToImagePath={variantToImagePath}
    />
  );

  return (
    <CardLink data-auto="agency-link" ref={cardItem}>
      {isRegistered ? (
        <Link
          routeName={Route.OfficePage}
          routeParams={{ id, source: 'madad_index' }}
          onClick={onCardClickEvent}
          hideLinkParams
        >
          {card}
        </Link>
      ) : card}
    </CardLink>
  );
};

const mapDispatchToProps = {
  openContactModal: setMadadContactModalState,
};

const AgencySmartCardWithEvents = withEvents<MadadAgencyCard & WithDispatchProps>((sendEvent, props) => ({
  onCardClickEvent: () => {
    sendEvent('madad_card_click', 'madad', {
      event: {
        agent_details: {
          office_id: props.id,
        },
      },
    });
  },
  onCardExposeEvent: (id: string) => {
    sendEvent('madad_card_agency_expose', 'madad', {
      event: {
        agency_details: {
          id,
        },
      },
    });
  },
  openContactModal: (params: SetMadadContactModalStatePayload) => {
    sendEvent('user_contact_me_form_open', 'user', {
      event: {
        agent_details: {
          id: params.id,
          name: params.agentName,
          office_id: params.officeId,
          office_name: params.officeName,
          phone_number: params.phone,
        },
        source: 'madad_card',
      },
    });
  },
}))(AgencySmartCard);

export default connect(null, mapDispatchToProps)(AgencySmartCardWithEvents);
